import {
    CollectionDataLoaded,
    ComponentServices,
    createRegisterableComponentWithData,
    DataLoaderGlobalParams,
    getSearchPublications,
    mapListPublication,
    TopicListingOptions,
} from '@news-mono/web-common'
import subMonths from 'date-fns/subMonths'
import { DataDefinition } from 'json-react-layouts-data-loader'
import React from 'react'
import { SearchResults } from './SearchResults'
import { Paging } from '../../data-controllers/Pagination/usePagination'

export interface SearchRouteProps {
    searchTerm: string
}

export interface SearchDataLoaderConfig {
    searchTerm: string
    options: TopicListingOptions
}

const loadSearchData = async (
    props: SearchDataLoaderConfig,
    services: DataLoaderGlobalParams,
): Promise<CollectionDataLoaded> => {
    if (!props.searchTerm) {
        return {
            loadMorePossible: false,
            kind: 'listing',
            publications: [],
            total: 0,
        }
    }

    const searchedPublications = await getSearchPublications(
        services,
        props.searchTerm,
        {
            ...props.options,
            // only searches the past month for db performance reasons
            publishedSince: subMonths(new Date(), 1).toISOString(),
            paging: props.options.paging ?? { pageSize: 20 },
        },
    )
    const mappedListing = searchedPublications.documents.map(
        (listPublication) => mapListPublication(listPublication),
    )
    return {
        loadMorePossible: searchedPublications.morePublicationsAvailable,
        kind: 'listing',
        publications: mappedListing,
        total: searchedPublications.total,
    }
}

const searchDataDefinitionLoader: DataDefinition<
    SearchDataLoaderConfig,
    CollectionDataLoaded,
    ComponentServices
> = {
    // Requires custom cache key so search terms will update when searching on the search page. Search.
    useRuntimeParams: (props) => ({
        searchTerm: JSON.stringify(props.searchTerm),
    }),
    loadData: (props, services) => loadSearchData(props, services),
}

export const SearchRegistration = createRegisterableComponentWithData(
    'search',
    searchDataDefinitionLoader,
    (props: SearchRouteProps, data, services) => {
        const setPage = (page: number) => {
            if (data.dataDefinitionArgs?.options.paging) {
                data.dataDefinitionArgs.options.paging.page = page
            }
        }

        if (data.loaded) {
            return (
                <SearchResults
                    onEvent={services.onEvent}
                    results={data.result}
                    paging={data.dataDefinitionArgs?.options.paging as Paging}
                    setPage={setPage}
                    searchTerm={props.searchTerm}
                />
            )
        }
        return null
    },
)
